import {skipToken} from '@reduxjs/toolkit/query';
import {
  Alert,
  Attributes,
  AttributesRow,
  Button,
  ButtonGroup,
  Checkbox,
  closeCurrentDialog,
  DataStatus,
  NumberInput,
  Separator,
  TextInput,
} from 'platform/components';
import {Box, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetBranchQuery} from '@dms/api/core';
import {
  useGetVehicleMileageQuery,
  useGetVehicleQuery,
  usePartialUpdateVehicleMutation,
} from '@dms/api/sales';
import {useGetCarPassConfigQuery} from '@dms/api/settings';
import {PatchVehicleRequestBody} from '@dms/api/shared';
import {useCreateCarPassRequestMutation} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, handleApiError} from '@dms/shared';

import {
  getApiDateString,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
  useBoolean,
  valueOrPlaceholder,
} from 'shared/web';

import {useCarPassForm} from '../hooks/useCarPassForm';
import {CarPassSubmitSuccessContent} from './CarPassSubmitSuccessContent';

const DIALOG_MODE = {
  REVIEW: 'REVIEW',
  FORM: 'FORM',
} as const;

type DialogMode = (typeof DIALOG_MODE)[keyof typeof DIALOG_MODE];

export interface CarPassSubmitDialogProps extends RequiredTestIdProps {
  vehicleId: string;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function CarPassSubmitDialog(props: CarPassSubmitDialogProps) {
  const formatNumber = useFormatNumber();
  const formatDateTime = useDateTimeFormatter();

  const [isConsented, setIsConsented] = useState(false);
  const [isSubmitted, setSubmitted] = useBoolean(false);
  const [mode, setMode] = useState<DialogMode>(DIALOG_MODE.REVIEW);

  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery({vehicleId: props.vehicleId});

  const branchId = vehicle?.branchId;
  const {data: branch, isLoading: isBranchLoading} = useGetBranchQuery(
    isNotNil(branchId) ? {branchId} : skipToken
  );

  const {
    data: carPassConfig,
    isLoading: isCarPassConfigLoading,
    isError: isCarPassConfigError,
  } = useGetCarPassConfigQuery(isNotNil(branchId) ? {branchId} : skipToken);

  const {data: vehicleMileage} = useGetVehicleMileageQuery({vehicleId: props.vehicleId});
  const [patchVehicle, {isLoading: isPatching}] = usePartialUpdateVehicleMutation();
  const [createCarPassRequest, {isLoading: isCreatingCarPass}] = useCreateCarPassRequestMutation();

  const isSubmitting = isPatching || isCreatingCarPass;

  const vin = vehicle?.vin ?? null;
  const mileage = vehicle?.state?.mileage ?? null;
  const registrationPlate = vehicle?.state?.registrationPlate ?? null;
  const vatNumber = carPassConfig?.credentials?.enterpriseOrganisation ?? null;

  const hasMissingVin = isNilOrEmpty(vin);
  const hasMissingMileage = isNil(mileage);

  const form = useCarPassForm({hasMissingVin, hasMissingMileage});

  const mileageReadDate = isNotNil(vehicleMileage?.datedOn)
    ? parseDate(vehicleMileage.datedOn)
    : null;

  // Sent to the BE as `mileageReadoutDate` so the Car-Pass odometer reading is
  // registered with the date the mileage was actually read (the same date shown
  // as the read date), not the request submission date. When unavailable, the
  // BE defaults to today.
  const mileageReadoutDate = isNotNil(mileageReadDate)
    ? getApiDateString(mileageReadDate)
    : undefined;

  const handleSend = () => {
    if ((hasMissingVin || hasMissingMileage) && mode === DIALOG_MODE.REVIEW) {
      setMode(DIALOG_MODE.FORM);
      form.validate();
      return;
    }

    const {isValid} = form.validate();
    if (!isValid) {
      return;
    }

    if (mode === DIALOG_MODE.FORM) {
      return patchVehicle({
        vehicleId: props.vehicleId,
        patchVehicleRequestBody: buildPatchBody(form),
      })
        .unwrap()
        .then(() => createCarPassRequest({vehicleId: props.vehicleId, mileageReadoutDate}).unwrap())
        .then(setSubmitted)
        .catch(handleApiError);
    }

    return createCarPassRequest({vehicleId: props.vehicleId, mileageReadoutDate})
      .unwrap()
      .then(setSubmitted)
      .catch(handleApiError);
  };

  const missingFieldsWarningTitle = match([hasMissingVin, hasMissingMileage])
    .with([true, true], () => i18n.t('page.carPass.information.missingVinAndMileageWarning'))
    .with([true, false], () => i18n.t('page.carPass.information.missingVinWarning'))
    .with([false, true], () => i18n.t('page.carPass.information.missingMileageWarning'))
    .otherwise(() => null);

  const readDateLabel = isNotNil(mileageReadDate)
    ? formatDateTime('dateShort', mileageReadDate)
    : EMPTY_PLACEHOLDER;

  const vinRow: AttributesRow =
    mode === DIALOG_MODE.FORM && hasMissingVin
      ? {
          label: i18n.t('entity.vehicle.labels.vin'),
          content: (
            <TextInput
              value={form.vinInput}
              onChange={form.setVinInput}
              errorMessage={form.vinError}
              maxLength={form.vinLength}
              isCounterVisible
              isAutoFocused
              data-testid={suffixTestId('vinInput', props)}
            />
          ),
        }
      : {label: i18n.t('entity.vehicle.labels.vin'), value: valueOrPlaceholder(vin)};

  const mileageRow: AttributesRow =
    mode === DIALOG_MODE.FORM && hasMissingMileage
      ? {
          label: i18n.t('page.carPass.labels.currentMileage'),
          content: (
            <NumberInput
              value={form.mileageInput}
              onChange={form.setMileageInput}
              suffix={i18n.t('general.metric.km')}
              errorMessage={form.mileageError}
              isAutoFocused={!hasMissingVin}
              minStepperValue={1}
              data-testid={suffixTestId('mileageInput', props)}
            />
          ),
        }
      : {
          label: i18n.t('page.carPass.labels.currentMileage'),
          value: isNotNil(mileage)
            ? `${formatNumber(mileage)} ${i18n.t('general.metric.km')}`
            : EMPTY_PLACEHOLDER,
        };

  const vehicleRows: AttributesRow[] = [
    vinRow,
    mileageRow,
    {
      label: i18n.t('page.carPass.labels.readDate'),
      value: readDateLabel,
    },
  ];

  const requestRows: AttributesRow[] = [
    {
      label: i18n.t('page.carPass.labels.branch'),
      value: valueOrPlaceholder(branch?.name),
    },
    {
      label: i18n.t('page.carPass.labels.vat'),
      value: valueOrPlaceholder(vatNumber),
    },
  ];

  if (isSubmitted) {
    return (
      <CarPassSubmitSuccessContent
        vehicleTitle={vehicle?.title}
        mileage={mileage}
        data-testid={suffixTestId('success', props)}
      />
    );
  }

  return (
    <VStack spacing={4}>
      <DataStatus
        isLoading={isVehicleLoading || isBranchLoading || isCarPassConfigLoading}
        isError={isVehicleError || isCarPassConfigError}
        minHeight={40}
      >
        <Alert variant="info" title={i18n.t('page.carPass.information.submitInfo')} />
        <Show when={isNotNil(vehicle)}>
          <VStack spacing={2}>
            <Heading size={5}>{i18n.t('page.carPass.labels.sectionVehicle')}</Heading>
            <Box padding={3} borderRadius="medium" backgroundColor="palettes.neutral.10.100">
              <VStack spacing={1}>
                <HStack justify="space-between" align="center">
                  <Heading size={5}>{vehicle?.title}</Heading>
                  <Show when={isNotNilOrEmpty(registrationPlate)}>
                    <Box
                      padding={1}
                      borderRadius="small"
                      backgroundColor="palettes.white.10.100"
                      border="1px solid"
                      borderColor="palettes.neutral.40.100"
                    >
                      <Text size="small" alternative>
                        {registrationPlate}
                      </Text>
                    </Box>
                  </Show>
                </HStack>
                <Text size="xSmall" color="secondary">
                  {[vehicle?.firstRegistrationOnYear, vehicle?.fuelType, vehicle?.bodyStyle]
                    .filter(isNotNilOrEmpty)
                    .join(' · ')}
                </Text>
              </VStack>
            </Box>
            <Attributes rows={vehicleRows} data-testid={suffixTestId('vehicle', props)} />
          </VStack>

          <Separator />

          <VStack spacing={2}>
            <Heading size={5}>{i18n.t('page.carPass.labels.sectionRequest')}</Heading>
            <Attributes rows={requestRows} data-testid={suffixTestId('request', props)} />
          </VStack>

          <Show when={mode === DIALOG_MODE.FORM && isNotNil(missingFieldsWarningTitle)}>
            <Alert
              variant="warning"
              title={missingFieldsWarningTitle ?? ''}
              data-testid={suffixTestId('missingFieldsWarning', props)}
            />
          </Show>

          <Checkbox
            value={isConsented}
            onChange={setIsConsented}
            label={i18n.t('page.carPass.labels.submitConsent')}
            data-testid={suffixTestId('consent', props)}
          />
        </Show>
      </DataStatus>

      <ButtonGroup align="right">
        <Button
          variant="secondary"
          leftIcon="navigation/close"
          title={i18n.t('general.actions.cancel')}
          onClick={closeCurrentDialog}
          data-testid={suffixTestId('cancel', props)}
        />
        <Button
          variant="primary"
          title={i18n.t('page.carPass.actions.send')}
          isLoading={isSubmitting}
          isDisabled={!isConsented}
          onClick={handleSend}
          data-testid={suffixTestId('submit', props)}
        />
      </ButtonGroup>
    </VStack>
  );
}

const buildPatchBody = (form: {vinInput: string | null; mileageInput: number | null}) => {
  const body: PatchVehicleRequestBody = {};
  const trimmedVin = form.vinInput?.trim();

  if (isNotNilOrEmpty(trimmedVin)) {
    body.vin = trimmedVin;
  }
  if (isNotNil(form.mileageInput)) {
    body.state = {mileage: form.mileageInput};
  }

  return body;
};
